import type { FileDetails } from "@/types/types";
import { apiClient } from "@/services/apiServices";
import axios from "axios";
import imageCompression from "browser-image-compression";
import compressionWorkerUrl from "browser-image-compression/dist/browser-image-compression.js?url";
import { sanitizeFileName } from "./sanitizeFileName";

export const UploadFileToS3 = async (
  file: FileDetails,
  bucket_name = "passionbits-core-files",
  options?: { timeoutMs?: number; onProgress?: (percent: number) => void },
): Promise<FileDetails> => {
  const controller = new AbortController();
  // Bound preparation and transfer together, with more time for large videos.
  const timeoutMs =
    options?.timeoutMs ?? (file.type.startsWith("image/") ? 120_000 : 600_000);
  let timeoutId: ReturnType<typeof setTimeout>;
  const deadline = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      controller.abort();
      reject(
        new Error("Upload timed out. Please check your connection and retry."),
      );
    }, timeoutMs);
  });

  const upload = async (): Promise<FileDetails> => {
    let body: File;
    if (file.file instanceof File) {
      body = file.file;
    } else {
      const response = await fetch(file.local_url, {
        signal: controller.signal,
      });
      if (!response.ok)
        throw new Error("Cannot read this file. Please select it again.");
      body = new File([await response.blob()], file.name, {
        type: file.type,
        lastModified: file.lastModified,
      });
    }
    if (!body.size)
      throw new Error("This file is empty. Please select another file.");

    // Some browsers leave the MIME type blank for local MOV files. Preserve a
    // usable content type for the presigned URL request and the S3 upload.
    const contentType =
      body.type ||
      (body.name.toLowerCase().endsWith(".mov")
        ? "video/quicktime"
        : body.name.toLowerCase().endsWith(".mp4")
          ? "video/mp4"
          : "application/octet-stream");

    // Avoid recompressing small images and keep the MIME type consistent.
    if (
      ["image/jpeg", "image/png", "image/webp"].includes(body.type) &&
      body.size > 512 * 1024
    ) {
      try {
        const compressed = await imageCompression(body, {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 1920,
          maxIteration: 5,
          useWebWorker: true,
          // Serve the worker library with the app instead of waiting on a CDN.
          libURL: new URL(compressionWorkerUrl, window.location.href).href,
          fileType: body.type,
          signal: controller.signal,
        });
        if (compressed.size > 0 && compressed.size < body.size)
          body = compressed;
      } catch (error) {
        if (controller.signal.aborted) throw error;
        // Compression is optional; fall back to the original on codec failure.
      }
    }
    controller.signal.throwIfAborted();

    const key = `creator-dashboard-files/${crypto.randomUUID()}-${sanitizeFileName(
      file.name,
    )}`;
    const response = await apiClient.post(
      "/upload/get-presigned-url",
      {
        fileName: key,
        fileType: contentType,
        fileSize: body.size,
        bucket_name,
      },
      { signal: controller.signal },
    );
    const url = response.data?.data;
    if (typeof url !== "string" || !url.startsWith("https://")) {
      throw new Error("Could not start the upload. Please try again.");
    }
    controller.signal.throwIfAborted();

    // Do not forward API authentication headers to S3.
    await axios.put(url, body, {
      headers: { "Content-Type": contentType },
      signal: controller.signal,
      onUploadProgress: ({ loaded, total }) => {
        if (total)
          options?.onProgress?.(
            Math.min(99, Math.round((loaded * 100) / total)),
          );
      },
    });
    options?.onProgress?.(100);

    return {
      name: key,
      size: body.size,
      type: contentType,
      lastModified: file.lastModified,
      webkitRelativePath: file.webkitRelativePath,
      local_url: file.local_url,
      live_url:
        bucket_name === "passionbits-core-files"
          ? `https://assets.passionbits.io/${key}`
          : url.split("?")[0],
    };
  };

  try {
    return await Promise.race([upload(), deadline]);
  } finally {
    clearTimeout(timeoutId!);
  }
};
